<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { ref, watch } from 'vue'
import BopPicker from '@/components/input/BopPicker.vue'
import ModulePicker from '@/components/input/ModulePicker.vue'
import SitePicker from '@/components/input/SitePicker.vue'
import ProductConfigurationPicker from '@/components/input/ProductConfigurationPicker.vue'
import ProductPicker from '@/components/input/ProductPicker.vue'
import useAsyncDialog from '@/components/dialogs/useAsyncDialog'
import { DuplicateModuleMutation, DuplicateModuleMutationVariables } from '@/generated/graphql'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'

const props = defineProps<{
  productId: string
  targetBopId: string
  initialConfigId?: string
  initialSiteId?: string
}>()
const emit = defineEmits<{
  duplicated: []
}>()

const { t } = useI18n()
const { openAsyncDialog, asyncDialogResolve, asyncDialogLoading, asyncDialogIsOpen } =
  useAsyncDialog<() => Promise<void>>()

const selectedProductId = ref<string | undefined>(props.productId)
const configId = ref<string | undefined>(props.initialConfigId)
const siteId = ref<string | undefined>(props.initialSiteId)
const bopId = ref<string | undefined>(props.targetBopId)
const moduleId = ref<string>()
watch(asyncDialogIsOpen, (v) => {
  if (v) {
    selectedProductId.value = props.productId
    configId.value = props.initialConfigId
    siteId.value = props.initialSiteId
    bopId.value = props.targetBopId
    moduleId.value = undefined
  }
})

const copyMutation = useMutation<DuplicateModuleMutation, DuplicateModuleMutationVariables>(gql`
  mutation DuplicateModule($command: DuplicateModule!) {
    product {
      duplicateModule(command: $command) {
        id
      }
    }
  }
`)

const notifySaved = ref(false)
const save = async () => {
  if (!moduleId.value || !asyncDialogResolve.value) {
    return
  }

  asyncDialogResolve.value(async () => {
    await copyMutation.mutate({
      command: {
        fromBOPId: bopId.value as string,
        fromModuleId: moduleId.value as string,
        toBOPId: props.targetBopId,
      },
    })
    notifySaved.value = true
    emit('duplicated')
  })
}

defineExpose({ open: openAsyncDialog })
defineOptions({ inheritAttrs: false })
</script>

<template>
  <template v-if="asyncDialogResolve">
    <v-dialog v-model="asyncDialogIsOpen" scrollable width="500" v-bind="$attrs">
      <v-card>
        <v-card-title>{{ t('component.duplicateModule.title') }}</v-card-title>
        <v-card-text>
          <product-picker
            v-model="selectedProductId"
            :label="t('entity.product.singular')"
            with-archived
            required
          />
          <product-configuration-picker
            v-model="configId"
            :product-id="selectedProductId"
            with-archived
            required
          />
          <site-picker v-model="siteId" :label="t('entity.site.singular')" required />
          <bop-picker
            v-model="bopId"
            :product-configuration-id="configId"
            :site-id="siteId"
            required
          />
          <module-picker v-model="moduleId" :bop-id="bopId" required />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="asyncDialogResolve()">{{ t('button.cancel') }}</v-btn>
          <v-btn :disabled="!moduleId" :loading="asyncDialogLoading" @click="save">
            {{ t('button.copy') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
